/**
 * Databender effects — raw BMP byte corruption.
 *
 * Ported from https://github.com/snicker02/Databender (index.html)
 * These effects operate on raw 24-bit BMP file bytes: they encode the
 * canvas to a real BMP, manipulate the bytes directly (databending),
 * then decode the result back to ImageData.
 *
 * Each effect: (bytes, info, params, seed) => Uint8Array
 *   bytes     — Uint8Array of the full BMP file (header + pixel data)
 *   info      — { width, height, dataOffset, stride }
 *   params    — { key: value } from the bridge
 *   seed      — uint32 RNG seed
 * Returns a new Uint8Array (may be different length than input).
 */

function mulberry32(a) {
    return function () {
        a |= 0; a = a + 0x6D2B79F5 | 0;
        let t = Math.imul(a ^ a >>> 15, 1 | a);
        t = t + Math.imul(t ^ t >>> 7, 61 | t) ^ t;
        return ((t ^ t >>> 14) >>> 0) / 4294967296;
    };
}

function hashStr(s) {
    let h = 2166136261;
    for (let i = 0; i < s.length; i++) { h ^= s.charCodeAt(i); h = Math.imul(h, 16777619); }
    return h >>> 0;
}

function clamp(v, a, b) { return v < a ? a : v > b ? b : v; }

// ---- shared helpers ----

function rotRow(b, base, W, k, len) {
    k = (((k % W) + W) % W);
    if (!k || base + W * 3 > len) return;
    const tmp = new Uint8Array(W * 3);
    for (let x = 0; x < W; x++) {
        const sx = (x + k) % W, q = sx * 3;
        tmp[x * 3] = b[base + q]; tmp[x * 3 + 1] = b[base + q + 1]; tmp[x * 3 + 2] = b[base + q + 2];
    }
    for (let x = 0; x < W * 3; x++) b[base + x] = tmp[x];
}

function sortPixelLine(b, start, step, count, t, len) {
    let i = 0;
    while (i < count) {
        let p = start + i * step;
        if (p + 2 >= len) break;
        let l = 0.299 * b[p + 2] + 0.587 * b[p + 1] + 0.114 * b[p];
        if (l < t) { i++; continue; }
        let j = i; const seg = [];
        while (j < count) {
            const q = start + j * step; if (q + 2 >= len) break;
            const lum = 0.299 * b[q + 2] + 0.587 * b[q + 1] + 0.114 * b[q];
            if (lum < t) break;
            seg.push([lum, b[q], b[q + 1], b[q + 2]]); j++;
        }
        seg.sort((a, c) => a[0] - c[0]);
        for (let k = 0; k < seg.length; k++) {
            const w = start + (i + k) * step;
            if (w + 2 < len) { b[w] = seg[k][1]; b[w + 1] = seg[k][2]; b[w + 2] = seg[k][3]; }
        }
        i = j === i ? i + 1 : j;
    }
}

function swapBlock(b, off, stride, x0, y0, x1, y1, bs, len) {
    for (let dy = 0; dy < bs; dy++) {
        const r0 = off + (y0 + dy) * stride + x0 * 3, r1 = off + (y1 + dy) * stride + x1 * 3;
        for (let dx = 0; dx < bs * 3; dx++) {
            const p = r0 + dx, q = r1 + dx;
            if (p < len && q < len) { const t = b[p]; b[p] = b[q]; b[q] = t; }
        }
    }
}

const SWAP_PERMS = { rb: [2, 1, 0], rg: [0, 2, 1], gb: [1, 0, 2], rot: [1, 2, 0] };

// ---- effect definitions ----
// Each: { id, label, params: [{ id, label, type, min, max, step, default, options? }], apply }

const EFFECTS = [

    {
        id: 'noise', label: 'Byte Noise',
        params: [{ id: 'amt', label: 'Amount', type: 'range', min: 0, max: 100, step: 1, default: 15 }],
        apply(bytes, info, p, seed) {
            const amt = p.amt / 100, r = mulberry32((seed ^ hashStr('noise')) >>> 0);
            const [s, e] = [info.dataOffset, bytes.length];
            const b = bytes.slice();
            for (let i = s; i < e; i++) if (r() < amt) b[i] = (r() * 256) | 0;
            return b;
        }
    },

    {
        id: 'bitrot', label: 'Bit Rot',
        params: [{ id: 'bits', label: 'Bits', type: 'range', min: 1, max: 7, step: 1, default: 3 }],
        apply(bytes, info, p) {
            const n = p.bits, [s, e] = [info.dataOffset, bytes.length], b = bytes.slice();
            for (let i = s; i < e; i++) { const v = b[i]; b[i] = ((v << n) | (v >>> (8 - n))) & 255; }
            return b;
        }
    },

    {
        id: 'xor', label: 'XOR',
        params: [{ id: 'key', label: 'Key', type: 'range', min: 0, max: 255, step: 1, default: 170 }],
        apply(bytes, info, p) {
            const key = [p.key & 255], [s, e] = [info.dataOffset, bytes.length], b = bytes.slice();
            for (let i = s, k = 0; i < e; i++, k++) b[i] ^= key[k % key.length];
            return b;
        }
    },

    {
        id: 'byte-shift', label: 'Byte Shift',
        params: [{ id: 'amt', label: 'Amount', type: 'range', min: 1, max: 1000, step: 1, default: 50 }],
        apply(bytes, info, p) {
            const n = p.amt, [s, e] = [info.dataOffset, bytes.length], b = bytes.slice(), L = e - s;
            if (L <= 0) return b;
            const t = b.slice(s, e), out = new Uint8Array(L);
            for (let i = 0; i < L; i++) out[i] = t[(i + n) % L];
            b.set(out, s);
            return b;
        }
    },

    {
        id: 'byte-sort', label: 'Byte Sort',
        params: [{ id: 'win', label: 'Window', type: 'range', min: 2, max: 500, step: 1, default: 50 }],
        apply(bytes, info, p) {
            const win = p.win, [s, e] = [info.dataOffset, bytes.length], b = bytes.slice();
            for (let i = s; i < e; i += win) {
                const end = Math.min(i + win, e);
                const seg = Array.from(b.subarray(i, end)).sort((a, c) => a - c);
                b.set(seg, i);
            }
            return b;
        }
    },

    {
        id: 'byte-reverse', label: 'Byte Reverse',
        params: [],
        apply(bytes, info) {
            const [s, e] = [info.dataOffset, bytes.length], b = bytes.slice();
            for (let i = s, j = e - 1; i < j; i++, j--) { const t = b[i]; b[i] = b[j]; b[j] = t; }
            return b;
        }
    },

    {
        id: 'channel-noise', label: 'Channel Noise',
        params: [
            { id: 'amt', label: 'Amount', type: 'range', min: 0, max: 100, step: 1, default: 20 },
            { id: 'chan', label: 'Channel', type: 'select', options: ['B', 'G', 'R'], default: 'R' }
        ],
        apply(bytes, info, p, seed) {
            const amt = p.amt / 100, ch = ['B', 'G', 'R'].indexOf(p.chan), off = info.dataOffset;
            const [s, e] = [off, bytes.length], r = mulberry32((seed ^ hashStr('chan' + ch)) >>> 0), b = bytes.slice();
            for (let i = s; i < e; i++) if ((((i - off) % 3) + 3) % 3 === ch && r() < amt) b[i] = (r() * 256) | 0;
            return b;
        }
    },

    {
        id: 'echo', label: 'Echo Chunk',
        params: [{ id: 'inten', label: 'Intensity', type: 'range', min: 0, max: 100, step: 1, default: 30 }],
        apply(bytes, info, p, seed) {
            const inten = p.inten / 100, r = mulberry32((seed ^ hashStr('echo')) >>> 0);
            const [s, e] = [info.dataOffset, bytes.length], src = bytes, span = e - s;
            if (span < 8) return src.slice();
            const chunk = Math.max(3, Math.floor(span * 0.01 * (0.4 + inten)));
            const cs = s + Math.floor(r() * (span - chunk)), reps = 1 + Math.floor(r() * (1 + inten * 4));
            const piece = src.slice(cs, cs + chunk), out = [];
            for (let i = 0; i < cs + chunk; i++) out.push(src[i]);
            for (let n = 0; n < reps; n++) for (const v of piece) out.push(v);
            for (let i = cs + chunk; i < src.length; i++) out.push(src[i]);
            return Uint8Array.from(out);
        }
    },

    {
        id: 'move', label: 'Move Chunk',
        params: [{ id: 'inten', label: 'Intensity', type: 'range', min: 0, max: 100, step: 1, default: 30 }],
        apply(bytes, info, p, seed) {
            const inten = p.inten / 100, r = mulberry32((seed ^ hashStr('move')) >>> 0);
            const [s, e] = [info.dataOffset, bytes.length], src = bytes, span = e - s;
            if (span < 16) return src.slice();
            const chunk = Math.max(3, Math.floor(span * 0.02 * (0.4 + inten)));
            const cs = s + Math.floor(r() * (span - chunk)), piece = src.slice(cs, cs + chunk);
            const removed = new Uint8Array(src.length - chunk);
            removed.set(src.subarray(0, cs), 0); removed.set(src.subarray(cs + chunk), cs);
            const dst = clamp(s + Math.floor(r() * (removed.length - s)), s, removed.length);
            const out = new Uint8Array(removed.length + chunk);
            out.set(removed.subarray(0, dst), 0); out.set(piece, dst); out.set(removed.subarray(dst), dst + chunk);
            return out;
        }
    },

    {
        id: 'row-shift', label: 'Row Shift',
        params: [{ id: 'inten', label: 'Intensity', type: 'range', min: 0, max: 100, step: 1, default: 30 }],
        apply(bytes, info, p, seed) {
            const inten = p.inten / 100, r = mulberry32((seed ^ hashStr('rowshift')) >>> 0);
            const off = info.dataOffset, W = info.width, pad = (4 - ((W * 3) % 4)) % 4;
            const stride = W * 3 + pad, H = info.height, src = bytes, out = [];
            for (let i = 0; i < off; i++) out.push(src[i]);
            let ptr = off; const maxJit = Math.max(1, Math.floor(stride * 0.15 * inten));
            for (let y = 0; y < H; y++) {
                for (let x = 0; x < stride && ptr < src.length; x++) { out.push(src[ptr]); ptr++; }
                const jit = Math.floor(r() * maxJit);
                for (let j = 0; j < jit; j++) out.push((r() * 256) | 0);
            }
            while (ptr < src.length) { out.push(src[ptr]); ptr++; }
            return Uint8Array.from(out);
        }
    },

    {
        id: 'carve', label: 'Carve Chunk',
        params: [{ id: 'inten', label: 'Intensity', type: 'range', min: 0, max: 100, step: 1, default: 30 }],
        apply(bytes, info, p, seed) {
            const inten = p.inten / 100, r = mulberry32((seed ^ hashStr('carve')) >>> 0);
            const [s, e] = [info.dataOffset, bytes.length], src = bytes, span = e - s;
            if (span < 16) return src.slice();
            const chunk = Math.max(1, Math.floor(span * 0.02 * inten));
            const cs = s + Math.floor(r() * (span - chunk));
            const out = new Uint8Array(src.length - chunk);
            out.set(src.subarray(0, cs), 0); out.set(src.subarray(cs + chunk), cs);
            return out;
        }
    },

    {
        id: 'bitcrush', label: 'Bit Crush',
        params: [{ id: 'bits', label: 'Keep Bits', type: 'range', min: 1, max: 7, step: 1, default: 4 }],
        apply(bytes, info, p) {
            const k = p.bits, mask = (0xFF << (8 - k)) & 0xFF, [s, e] = [info.dataOffset, bytes.length], b = bytes.slice();
            for (let i = s; i < e; i++) b[i] &= mask;
            return b;
        }
    },

    {
        id: 'byte-add', label: 'Byte Add',
        params: [{ id: 'amt', label: 'Amount', type: 'range', min: -255, max: 255, step: 1, default: 50 }],
        apply(bytes, info, p) {
            const amt = p.amt, [s, e] = [info.dataOffset, bytes.length], b = bytes.slice();
            for (let i = s; i < e; i++) b[i] = (b[i] + amt) & 255;
            return b;
        }
    },

    {
        id: 'row-stutter', label: 'Row Stutter',
        params: [{ id: 'inten', label: 'Intensity', type: 'range', min: 0, max: 100, step: 1, default: 40 }],
        apply(bytes, info, p, seed) {
            const inten = p.inten / 100, r = mulberry32((seed ^ hashStr('stutter')) >>> 0);
            const off = info.dataOffset, W = info.width, H = info.height;
            const pad = (4 - ((W * 3) % 4)) % 4, stride = W * 3 + pad, b = bytes.slice(), len = b.length;
            if (H < 3) return b;
            const blk = Math.max(1, Math.floor(1 + r() * 6));
            const bandRows = Math.max(blk + 1, Math.floor(H * (0.15 + 0.5 * inten)));
            const maxStart = Math.max(1, H - blk - bandRows), startRow = Math.floor(r() * maxStart);
            let dst = startRow + blk; const endDst = Math.min(H, dst + bandRows); let k = 0;
            while (dst < endDst) {
                const sB = off + (startRow + (k % blk)) * stride, dB = off + dst * stride;
                for (let x = 0; x < stride; x++) { if (dB + x < len && sB + x < len) b[dB + x] = b[sB + x]; }
                dst++; k++;
            }
            return b;
        }
    },

    {
        id: 'slice-shuffle', label: 'Slice Shuffle',
        params: [{ id: 'inten', label: 'Intensity', type: 'range', min: 0, max: 100, step: 1, default: 40 }],
        apply(bytes, info, p, seed) {
            const inten = p.inten / 100, r = mulberry32((seed ^ hashStr('slice')) >>> 0);
            const off = info.dataOffset, W = info.width, H = info.height;
            const pad = (4 - ((W * 3) % 4)) % 4, stride = W * 3 + pad, b = bytes.slice(), len = b.length;
            const slices = Math.max(2, Math.floor(3 + r() * 20 * inten)), bandH = Math.ceil(H / slices);
            for (let sIdx = 0; sIdx < slices; sIdx++) {
                const y0 = sIdx * bandH, y1 = Math.min(H, y0 + bandH);
                const shiftPx = Math.round((r() * 2 - 1) * W * 0.5 * inten);
                if (!shiftPx) continue;
                for (let y = y0; y < y1; y++) rotRow(b, off + y * stride, W, shiftPx, len);
            }
            return b;
        }
    },

    {
        id: 'channel-swap', label: 'Channel Swap',
        params: [{ id: 'mode', label: 'Mode', type: 'select', options: ['R↔B', 'R↔G', 'G↔B', 'Rotate'], default: 'R↔B' }],
        apply(bytes, info, p) {
            const permMap = { 'R↔B': 'rb', 'R↔G': 'rg', 'G↔B': 'gb', 'Rotate': 'rot' };
            const perm = SWAP_PERMS[permMap[p.mode] || 'rb'], off = info.dataOffset, b = bytes.slice(), len = b.length;
            for (let ptr = off; ptr + 2 < len; ptr += 3) {
                const s0 = b[ptr], s1 = b[ptr + 1], s2 = b[ptr + 2], src = [s0, s1, s2];
                b[ptr] = src[perm[0]]; b[ptr + 1] = src[perm[1]]; b[ptr + 2] = src[perm[2]];
            }
            return b;
        }
    },

    {
        id: 'data-smear', label: 'Data Smear',
        params: [{ id: 'amt', label: 'Amount', type: 'range', min: 0, max: 100, step: 1, default: 30 }],
        apply(bytes, info, p, seed) {
            const amt = p.amt / 100, r = mulberry32((seed ^ hashStr('smear')) >>> 0);
            const off = info.dataOffset, W = info.width, H = info.height;
            const pad = (4 - ((W * 3) % 4)) % 4, stride = W * 3 + pad, b = bytes.slice(), len = b.length;
            for (let y = 0; y < H; y++) {
                const base = off + y * stride; if (base + W * 3 > len) break;
                let hold = -1, runLeft = 0;
                for (let x = 0; x < W; x++) {
                    const ptr = base + x * 3;
                    if (runLeft > 0 && hold >= 0) { b[ptr] = b[hold]; b[ptr + 1] = b[hold + 1]; b[ptr + 2] = b[hold + 2]; runLeft--; }
                    else if (r() < amt * 0.35) { hold = ptr; runLeft = 1 + Math.floor(r() * W * 0.3 * amt); }
                }
            }
            return b;
        }
    },

    {
        id: 'interlace', label: 'Interlace',
        params: [{ id: 'amt', label: 'Offset', type: 'range', min: 1, max: 50, step: 1, default: 5 }],
        apply(bytes, info, p) {
            const n = p.amt, off = info.dataOffset, W = info.width, H = info.height;
            const pad = (4 - ((W * 3) % 4)) % 4, stride = W * 3 + pad, b = bytes.slice(), len = b.length;
            if (!n) return b;
            for (let y = 0; y < H; y++) rotRow(b, off + y * stride, W, (y & 1) ? -n : n, len);
            return b;
        }
    },

    {
        id: 'block-shuffle', label: 'Block Shuffle',
        params: [{ id: 'inten', label: 'Intensity', type: 'range', min: 0, max: 100, step: 1, default: 30 }],
        apply(bytes, info, p, seed) {
            const inten = p.inten / 100, r = mulberry32((seed ^ hashStr('block')) >>> 0);
            const off = info.dataOffset, W = info.width, H = info.height;
            const pad = (4 - ((W * 3) % 4)) % 4, stride = W * 3 + pad, b = bytes.slice(), len = b.length;
            const bs = Math.max(4, Math.floor(8 + r() * 40)), cols = Math.floor(W / bs), rows = Math.floor(H / bs);
            if (cols < 2 || rows < 2) return b;
            const swaps = Math.floor(cols * rows * 0.5 * inten) + 1;
            for (let n = 0; n < swaps; n++) {
                const ax = Math.floor(r() * cols), ay = Math.floor(r() * rows);
                const bx = Math.floor(r() * cols), by = Math.floor(r() * rows);
                if (ax === bx && ay === by) continue;
                swapBlock(b, off, stride, ax * bs, ay * bs, bx * bs, by * bs, bs, len);
            }
            return b;
        }
    },

    {
        id: 'ghost', label: 'Ghost',
        params: [
            { id: 'dx', label: 'Offset', type: 'range', min: 1, max: 100, step: 1, default: 20 },
            { id: 'opac', label: 'Opacity', type: 'range', min: 0, max: 100, step: 1, default: 50 }
        ],
        apply(bytes, info, p) {
            const dx = p.dx, amt = p.opac / 100, inv = 1 - amt;
            const off = info.dataOffset, W = info.width, H = info.height;
            const pad = (4 - ((W * 3) % 4)) % 4, stride = W * 3 + pad, src = bytes, b = src.slice(), len = b.length;
            for (let y = 0; y < H; y++) {
                const base = off + y * stride; if (base + W * 3 > len) break;
                for (let x = 0; x < W; x++) {
                    const sx = x - dx; if (sx < 0 || sx >= W) continue;
                    const ptr = base + x * 3, q = base + sx * 3;
                    b[ptr] = (src[ptr] * inv + src[q] * amt) | 0;
                    b[ptr + 1] = (src[ptr + 1] * inv + src[q + 1] * amt) | 0;
                    b[ptr + 2] = (src[ptr + 2] * inv + src[q + 2] * amt) | 0;
                }
            }
            return b;
        }
    },

    {
        id: 'hue-rotate', label: 'Hue Rotate',
        params: [{ id: 'deg', label: 'Degrees', type: 'range', min: 0, max: 360, step: 1, default: 90 }],
        apply(bytes, info, p) {
            const a = p.deg * Math.PI / 180, cs = Math.cos(a), sn = Math.sin(a);
            const m = [
                0.213 + cs * 0.787 - sn * 0.213, 0.715 - cs * 0.715 - sn * 0.715, 0.072 - cs * 0.072 + sn * 0.928,
                0.213 - cs * 0.213 + sn * 0.143, 0.715 + cs * 0.285 + sn * 0.140, 0.072 - cs * 0.072 - sn * 0.283,
                0.213 - cs * 0.213 - sn * 0.787, 0.715 - cs * 0.715 + sn * 0.715, 0.072 + cs * 0.928 + sn * 0.072
            ];
            const off = info.dataOffset, b = bytes.slice(), len = b.length;
            for (let ptr = off; ptr + 2 < len; ptr += 3) {
                const B = b[ptr], G = b[ptr + 1], R = b[ptr + 2];
                let nr = R * m[0] + G * m[1] + B * m[2], ng = R * m[3] + G * m[4] + B * m[5], nb = R * m[6] + G * m[7] + B * m[8];
                b[ptr] = nb < 0 ? 0 : nb > 255 ? 255 : nb | 0;
                b[ptr + 1] = ng < 0 ? 0 : ng > 255 ? 255 : ng | 0;
                b[ptr + 2] = nr < 0 ? 0 : nr > 255 ? 255 : nr | 0;
            }
            return b;
        }
    },

    {
        id: 'sine-bands', label: 'Sine Bands',
        params: [
            { id: 'amp', label: 'Amplitude', type: 'range', min: 1, max: 255, step: 1, default: 50 },
            { id: 'freq', label: 'Frequency', type: 'range', min: 1, max: 100, step: 1, default: 20 }
        ],
        apply(bytes, info, p) {
            const amp = p.amp, freq = p.freq / 100;
            const off = info.dataOffset, W = info.width, H = info.height;
            const pad = (4 - ((W * 3) % 4)) % 4, stride = W * 3 + pad, b = bytes.slice(), len = b.length;
            for (let y = 0; y < H; y++) {
                const base = off + y * stride; if (base + W * 3 > len) break;
                for (let x = 0; x < W; x++) {
                    const ptr = base + x * 3, ph = freq * (x + y);
                    b[ptr] = (b[ptr] + Math.round(amp * Math.sin(ph + 4.188))) & 255;
                    b[ptr + 1] = (b[ptr + 1] + Math.round(amp * Math.sin(ph + 2.094))) & 255;
                    b[ptr + 2] = (b[ptr + 2] + Math.round(amp * Math.sin(ph))) & 255;
                }
            }
            return b;
        }
    },

    {
        id: 'pixel-sort', label: 'Pixel Sort (BMP)',
        params: [
            { id: 'amt', label: 'Threshold', type: 'range', min: 0, max: 100, step: 1, default: 50 },
            { id: 'vert', label: 'Vertical', type: 'select', options: ['No', 'Yes'], default: 'No' }
        ],
        apply(bytes, info, p) {
            const amt = p.amt / 100, vert = p.vert === 'Yes';
            const off = info.dataOffset, W = info.width, H = info.height;
            const pad = (4 - ((W * 3) % 4)) % 4, stride = W * 3 + pad, b = bytes.slice(), len = b.length;
            let lo = 255, hi = 0;
            for (let r = 0; r < H; r++) {
                const base = off + r * stride;
                for (let x = 0; x < W; x++) {
                    const ptr = base + x * 3; if (ptr + 2 >= len) break;
                    const l = 0.299 * b[ptr + 2] + 0.587 * b[ptr + 1] + 0.114 * b[ptr];
                    if (l < lo) lo = l; if (l > hi) hi = l;
                }
            }
            if (hi <= lo) return b;
            const cutoff = hi - amt * (hi - lo);
            if (vert) { for (let x = 0; x < W; x++) sortPixelLine(b, off + x * 3, stride, H, cutoff, len); }
            else { for (let r = 0; r < H; r++) sortPixelLine(b, off + r * stride, 3, W, cutoff, len); }
            return b;
        }
    },

    {
        id: 'wave', label: 'Wave',
        params: [
            { id: 'amp', label: 'Amplitude', type: 'range', min: 1, max: 100, step: 1, default: 20 },
            { id: 'freq', label: 'Frequency', type: 'range', min: 1, max: 100, step: 1, default: 30 }
        ],
        apply(bytes, info, p) {
            const amp = p.amp, freq = p.freq / 100;
            const off = info.dataOffset, W = info.width, H = info.height;
            const pad = (4 - ((W * 3) % 4)) % 4, stride = W * 3 + pad, b = bytes.slice(), len = b.length;
            const row = new Uint8Array(W * 3);
            for (let r = 0; r < H; r++) {
                const shift = Math.round(amp * Math.sin(freq * r)); if (!shift) continue;
                const base = off + r * stride; if (base + W * 3 > len) break;
                for (let x = 0; x < W * 3; x++) row[x] = b[base + x];
                for (let x = 0; x < W; x++) {
                    const sx = (((x + shift) % W) + W) % W, ptr = base + x * 3, q = sx * 3;
                    b[ptr] = row[q]; b[ptr + 1] = row[q + 1]; b[ptr + 2] = row[q + 2];
                }
            }
            return b;
        }
    },

    {
        id: 'rgb-shift', label: 'RGB Shift (BMP)',
        params: [
            { id: 'px', label: 'Pixels', type: 'range', min: 1, max: 100, step: 1, default: 10 },
            { id: 'chan', label: 'Channel', type: 'select', options: ['B', 'G', 'R'], default: 'R' }
        ],
        apply(bytes, info, p) {
            const px = p.px, ch = ['B', 'G', 'R'].indexOf(p.chan), off = info.dataOffset;
            const [s, e] = [off, bytes.length], src = bytes, b = src.slice(), len = src.length, shift = px * 3;
            for (let i = s; i < e; i++) {
                if ((((i - off) % 3) + 3) % 3 === ch) { const j = i + shift; b[i] = (j >= 0 && j < len) ? src[j] : 0; }
            }
            return b;
        }
    },

    {
        id: 'emboss', label: 'Emboss (BMP)',
        params: [],
        apply(bytes, info) {
            const off = info.dataOffset, W = info.width, H = info.height;
            const pad = (4 - ((W * 3) % 4)) % 4, stride = W * 3 + pad, src = bytes, b = src.slice(), len = b.length;
            for (let y = 0; y < H; y++) {
                const base = off + y * stride; if (base + W * 3 > len) break;
                for (let x = 0; x < W; x++) {
                    const ptr = base + x * 3, qx = x > 0 ? x - 1 : x, qy = y > 0 ? y - 1 : y, q = off + qy * stride + qx * 3;
                    for (let c = 0; c < 3; c++) { let v = 128 + (src[ptr + c] - src[q + c]); b[ptr + c] = v < 0 ? 0 : v > 255 ? 255 : v; }
                }
            }
            return b;
        }
    },

    // ---- chunk operations (deterministic, controlled by offset/length/dest) ----

    {
        id: 'chunk-move', label: 'Chunk Move',
        params: [
            { id: 'offset', label: 'Offset', type: 'range', min: 0, max: 100, step: 1, default: 20 },
            { id: 'length', label: 'Length', type: 'range', min: 1, max: 50, step: 1, default: 10 },
            { id: 'dest', label: 'Dest', type: 'range', min: 0, max: 100, step: 1, default: 60 }
        ],
        apply(bytes, info, p) {
            const [s, e] = [info.dataOffset, bytes.length], span = Math.max(1, e - s);
            const ss = clamp(s + Math.floor(p.offset / 100 * span), s, e - 1);
            let sl = Math.max(1, Math.floor(p.length / 100 * span)); sl = Math.min(sl, e - ss);
            const dd = clamp(s + Math.floor(p.dest / 100 * span), s, e);
            const src = bytes, piece = src.slice(ss, ss + sl);
            const removed = new Uint8Array(src.length - sl);
            removed.set(src.subarray(0, ss), 0); removed.set(src.subarray(ss + sl), ss);
            let d = dd <= ss ? dd : dd >= ss + sl ? dd - sl : ss; d = clamp(d, 0, removed.length);
            const out = new Uint8Array(removed.length + sl);
            out.set(removed.subarray(0, d), 0); out.set(piece, d); out.set(removed.subarray(d), d + sl);
            return out;
        }
    },

    {
        id: 'chunk-echo', label: 'Chunk Echo',
        params: [
            { id: 'offset', label: 'Offset', type: 'range', min: 0, max: 100, step: 1, default: 20 },
            { id: 'length', label: 'Length', type: 'range', min: 1, max: 50, step: 1, default: 10 },
            { id: 'dest', label: 'Dest', type: 'range', min: 0, max: 100, step: 1, default: 60 }
        ],
        apply(bytes, info, p) {
            const [s, e] = [info.dataOffset, bytes.length], span = Math.max(1, e - s);
            const ss = clamp(s + Math.floor(p.offset / 100 * span), s, e - 1);
            let sl = Math.max(1, Math.floor(p.length / 100 * span)); sl = Math.min(sl, e - ss);
            const dd = clamp(s + Math.floor(p.dest / 100 * span), s, e);
            const src = bytes, piece = src.slice(ss, ss + sl), d = clamp(dd, 0, src.length);
            const out = new Uint8Array(src.length + sl);
            out.set(src.subarray(0, d), 0); out.set(piece, d); out.set(src.subarray(d), d + sl);
            return out;
        }
    },

    {
        id: 'chunk-carve', label: 'Chunk Carve',
        params: [
            { id: 'offset', label: 'Offset', type: 'range', min: 0, max: 100, step: 1, default: 30 },
            { id: 'length', label: 'Length', type: 'range', min: 1, max: 50, step: 1, default: 10 }
        ],
        apply(bytes, info, p) {
            const [s, e] = [info.dataOffset, bytes.length], span = Math.max(1, e - s);
            const ss = clamp(s + Math.floor(p.offset / 100 * span), s, e - 1);
            let sl = Math.max(1, Math.floor(p.length / 100 * span)); sl = Math.min(sl, e - ss);
            const src = bytes;
            const out = new Uint8Array(src.length - sl);
            out.set(src.subarray(0, ss), 0); out.set(src.subarray(ss + sl), ss);
            return out;
        }
    },

    {
        id: 'chunk-rotate', label: 'Chunk Rotate',
        params: [
            { id: 'offset', label: 'Offset', type: 'range', min: 0, max: 100, step: 1, default: 20 },
            { id: 'length', label: 'Length', type: 'range', min: 1, max: 50, step: 1, default: 15 },
            { id: 'rot', label: 'Rotate', type: 'range', min: 0, max: 100, step: 1, default: 50 }
        ],
        apply(bytes, info, p) {
            const [s, e] = [info.dataOffset, bytes.length], span = Math.max(1, e - s);
            const ss = clamp(s + Math.floor(p.offset / 100 * span), s, e - 1);
            let sl = Math.max(1, Math.floor(p.length / 100 * span)); sl = Math.min(sl, e - ss);
            const b = bytes.slice();
            let k = Math.round((p.rot / 100) * sl); k = (((k % sl) + sl) % sl);
            if (k) { const tmp = b.slice(ss, ss + sl); for (let i = 0; i < sl; i++) b[ss + i] = tmp[(i + k) % sl]; }
            return b;
        }
    },

    {
        id: 'chunk-reverse', label: 'Chunk Reverse',
        params: [
            { id: 'offset', label: 'Offset', type: 'range', min: 0, max: 100, step: 1, default: 20 },
            { id: 'length', label: 'Length', type: 'range', min: 1, max: 50, step: 1, default: 15 }
        ],
        apply(bytes, info, p) {
            const [s, e] = [info.dataOffset, bytes.length], span = Math.max(1, e - s);
            const ss = clamp(s + Math.floor(p.offset / 100 * span), s, e - 1);
            let sl = Math.max(1, Math.floor(p.length / 100 * span)); sl = Math.min(sl, e - ss);
            const b = bytes.slice();
            for (let i = ss, j = ss + sl - 1; i < j; i++, j--) { const t = b[i]; b[i] = b[j]; b[j] = t; }
            return b;
        }
    },

];

// Build a map for quick lookup
const EFFECT_MAP = new Map(EFFECTS.map(e => [e.id, e]));

function getEffectIds() {
    return EFFECTS.map(e => e.id);
}

function getDefaultParams(effectId) {
    const effect = EFFECT_MAP.get(effectId);
    if (!effect) return {};
    const defaults = {};
    effect.params.forEach(p => { defaults[p.id] = p.default; });
    return defaults;
}

module.exports = { EFFECTS, EFFECT_MAP, getEffectIds, getDefaultParams };
